import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgMapsInfoWindowComponent } from '@ng-maps/core/lib/directives/info-window';
import {
  TerraDraw,
  TerraDrawFreehandMode,
  TerraDrawSelectMode,
  ValidateNotSelfIntersecting,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { GoogleComponent } from '@ng-maps/google';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectMapHelper from 'src/app/helpers/project-map-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Employee from 'src/app/model/product/employee';
import { Tour } from 'src/app/model/tour/tour';
import { GeocoderService } from 'src/app/services/geocoder.service';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { DoOnce } from 'src/app/subscription-helpers/subscriptions';
import IPBoardPlacementType, {
  IPBoardPlacementTypeChoicesArray,
} from '../../../../../model/product/ip-board/ip-board-placement-type';
import { EmployeeTourMapDirectionsCalculationService } from '../employee-tour-edit-modal/employee-tour-map/service/employee-tour-map-directions-calculation.service';
import { PreviewInfo } from '../employee-tour-list/employee-tour/helper/employee-tour-helper';
import { TourPlanningEventService } from '../service/tour-planning-event.service';
import {
  PreviewMultipleToursItem,
  PreviewSlot,
  TourPreviewStateService,
} from '../service/tour-preview-state.service';
import {
  GroupSelectedEvent,
  ImmoMapHelper,
  ItemGroup,
  LastSelectedPlanValues,
  PreviewMarkerSelectedEvent,
  ProcessUnassignedItemResult,
  TourPreviewMarker,
} from './helper/immo-map-helper';
import {
  ImmoMapControllerRequestService,
  ImmoMapFilterObject,
} from './services/immo-map-controller-request.service';
import { ImmoMapItem } from './services/model/immo-map-sub-project';
import { TourPreviewSelectorModalComponent } from './tour-preview-selector-modal/tour-preview-selector-modal.component';
import { Feature, Polygon } from 'geojson';
import { MarkerMultiselectModal } from './marker-multiselect-modal/marker-multiselect-modal.component';

@Component({
  selector: 'immo-map',
  templateUrl: './immo-map.component.html',
  styleUrls: ['./immo-map.component.scss'],
})
export class ImmoMapComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input()
  placers: Employee[];

  INPRESS_COORDINATES = ProjectMapHelper.INPRESS_COORDINATES;
  HIDE_POI_STYLE = ProjectMapHelper.HIDE_POI_STYLE;

  allSelected: boolean = true;
  withDateSelected: boolean;
  baseProjectSelected: boolean;
  adjustmentSelected: boolean;
  soldRentedSelected: boolean;
  repoSelected: boolean;
  taskSelected: boolean;
  notExecutedSelected: boolean;
  notInTourSelected: boolean;
  alreadyInTourSelected: boolean;

  companyName: string;
  companyNameChanged = new Subject<any>();

  selectedPlacementTypes: IPBoardPlacementType[] = [];
  PLACEMENT_TYPE_CHOICE_ARRAY = IPBoardPlacementTypeChoicesArray;

  selectedBranchFilter: string = null;
  branchPreviewStates: Map<string, boolean> = new Map();

  init = false;

  currentMapZoom: number = 14;

  private readonly APPOINTMENT_INFO_LATITUDE_BASE_EXPONENT: number = 5;
  private readonly PREVIEW_POPUP_LATITUDE_OFFSET: number = 8;
  appointmentInfoLatitudeOffset: number;
  previewPopupLatitudeOffset: number;

  currentIW: NgMapsInfoWindowComponent;
  previousIW: NgMapsInfoWindowComponent;

  itemGroups: ItemGroup[];
  itemGroupsInDrawnRegion: ItemGroup[] = [];
  coordsToGroup: Map<number, ItemGroup>;

  @ViewChild(GoogleComponent, { static: true })
  agmMap: GoogleComponent;
  map: google.maps.Map;
  terraDraw: TerraDraw | null = null;

  @ViewChild('mapContainerScrollAnchor') mapContainerScrollAnchor: ElementRef;

  getItemsSubject: Subject<any>;
  private initSubject = new Subject<boolean>();

  groupSelected = new ReplaySubject<GroupSelectedEvent>(1);
  itemAddedToGroup = new Subject<ItemGroup>();
  previewMarkerSelected = new ReplaySubject<PreviewMarkerSelectedEvent>(1);
  private lastSelectedPlanValues: LastSelectedPlanValues;

  previewingTours: boolean = false;
  previewSlots: PreviewSlot[] = [];
  previousIWPreviewSlot: PreviewSlot | null;

  private debouncedTourPreviewStateChangeObservable: Observable<PreviewSlot[]>;

  private centerMapOnItems: boolean = true;
  private itemBounds: google.maps.LatLngBounds;

  private tourPreviewBounds: google.maps.LatLngBounds;

  private loadMapSubscription;

  constructor(
    private immoMapControllerRequestService: ImmoMapControllerRequestService,
    private tourPlanningEventService: TourPlanningEventService,
    private tourPreviewStateService: TourPreviewStateService,
    private employeeTourMapDirectionsCalculationService: EmployeeTourMapDirectionsCalculationService,
    private geocoderService: GeocoderService,
    private modalService: ModalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.debouncedTourPreviewStateChangeObservable =
      this.tourPreviewStateService
        .getTourPreviewStateChangeObservable()
        .pipe(debounceTime(250), takeUntil(this.destroyThisComponent));

    this.calculateAppointmentInfoLatitudeOffset();
    this.getItemsSubject = new Subject<any>();

    this.loadMap();
    this.addSubscription(
      this.companyNameChanged,
      () => this.getItemsSubject.next(),
      GeneralHelper.noop,
      500
    );
    this.addSubscription(
      this.tourPlanningEventService.getSubProjectWasUnassignedSubject(),
      () => this.getItemsSubject.next()
    );
    this.addSubscription(
      this.debouncedTourPreviewStateChangeObservable,
      (previewSlots: PreviewSlot[]) => {
        this.processTourPreviewStateChange(previewSlots);
      }
    );
  }

  loadMap(reload?: boolean): void {
    if (reload) {
      this.destroySubscription(this.loadMapSubscription);
    }
    this.loadMapSubscription = this.addSubscription(
      this.getItemsSubject.pipe(
        startWith([]),
        switchMap(async () => {
          if (!reload) {
            this.setAndEmitInit(false);
          }
          return await this.getItemRequestPromise();
        }),
        catchError(() => {
          return of([]);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ImmoMapItem[]) => {
        const constructItemsGroupsResult =
          ImmoMapHelper.constructSameCoordsArray(res);
        this.itemGroups = constructItemsGroupsResult.itemGroups;
        this.coordsToGroup =
          constructItemsGroupsResult.coordsToGroup == null
            ? new Map<number, ItemGroup>()
            : constructItemsGroupsResult.coordsToGroup;
        if (constructItemsGroupsResult.latLngBounds != null) {
          this.itemBounds = constructItemsGroupsResult.latLngBounds;
          if (this.centerMapOnItems && !reload) {
            this.centerItems();
          }
        }
        this.setAndEmitInit(true);
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getItemsSubject != null) {
      this.getItemsSubject.complete();
    }
    if (this.initSubject != null) {
      this.initSubject.complete();
    }
    if (this.companyNameChanged != null) {
      this.companyNameChanged.complete();
    }
    if (this.groupSelected != null) {
      this.groupSelected.complete();
    }
    if (this.itemAddedToGroup != null) {
      this.itemAddedToGroup.complete();
    }
    if (this.previewMarkerSelected != null) {
      this.previewMarkerSelected.complete();
    }
    this.tourPreviewStateService.resetPreviewStates();
    this.tourPreviewStateService.resetDirectionRenderers();

    if (this.terraDraw !== null) {
      this.terraDraw.clear();
      this.terraDraw.off('finish', this.onFinishDrawing.bind(this));
      this.terraDraw = null;
    }
  }

  ngAfterViewInit(): void {
    DoOnce(this.agmMap.mapReady, (map: google.maps.Map) => {
      this.map = map;
      this.tourPreviewStateService.setDirectionRendererMaps(map);
      this.centerMapOnBelgium();
      this.addSubscription(this.initSubject, (b) => {
        if (!b) {
          if (this.itemGroups != null) {
            this.itemGroups.length = 0;
          }
          this.centerMapOnBelgium();
        }
      });
    });
  }

  private getItemRequestPromise(): Promise<ImmoMapItem[]> {
    const filterObject: ImmoMapFilterObject = ImmoMapHelper.getFilterObject(
      this.companyName,
      this.selectedPlacementTypes,
      this.allSelected,
      this.withDateSelected,
      this.baseProjectSelected,
      this.adjustmentSelected,
      this.soldRentedSelected,
      this.repoSelected,
      this.taskSelected,
      this.notExecutedSelected,
      this.notInTourSelected,
      this.alreadyInTourSelected
    );
    return this.immoMapControllerRequestService.getImmoItems(filterObject);
  }

  private centerMapOnBelgium(): void {
    this.map.setZoom(8);
    this.map.panTo(ProjectMapHelper.BELGIUM_GEOGRAPHICAL_CENTER);
  }

  private setAndEmitInit(value: boolean) {
    this.init = value;
    if (!value) {
      if (this.currentIW != null) {
        this.currentIW.close();
        this.currentIW = null;
      }
      if (this.previousIW != null) {
        this.previousIW.close();
        this.previousIW = null;
      }
    }
    this.initSubject.next(value);
  }

  openTourPreviewModal(): void {
    this.modalService.openComponentInModal(
      TourPreviewSelectorModalComponent,
      [],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.requestedTours,
        async (tours: Tour[]) => {
          const items: PreviewMultipleToursItem[] =
            await ImmoMapHelper.getPreviewMultipleToursItems(
              tours,
              this.geocoderService
            );
          if (!CollectionHelper.isPopulated(items)) {
            this.tourPreviewStateService.resetPreviewStates();
          } else {
            this.tourPreviewStateService.setMultipleTours(items);
          }
        }
      ),
      { width: '97vw' }
    );
  }

  openMultiselectModal(): void {
    this.modalService.openComponentInModal(MarkerMultiselectModal, [], null, {
      width: '97vw',
    });
  }

  resetPreviews(): void {
    this.tourPreviewStateService.resetPreviewStates();
  }

  private onFinishDrawing(_id: string | number): void {
    const snapshot = this.terraDraw.getSnapshot();
    const polygons = snapshot.filter(
      (feature) => feature.geometry.type === 'Polygon'
    ) as Feature<Polygon, Record<string, any>>[];

    if (polygons.length === 0) {
      this.itemGroupsInDrawnRegion = [];
      return;
    }

    // We select the last (and only) polygon as the drawn region sicne clear is always called.
    const polygon = polygons[polygons.length - 1];

    const coords = polygon.geometry.coordinates[0];

    // Find contained markers using point-in-polygon check
    const contained = this.itemGroups.filter((group) => {
      const point = { lat: group.lat, lng: group.lng };
      return google.maps.geometry.poly.containsLocation(
        point,
        new google.maps.Polygon({
          paths: coords.map((c) => ({ lat: c[1], lng: c[0] })),
        })
      );
    });

    this.itemGroupsInDrawnRegion = contained;

    // Switch to select mode after drawing
    this.terraDraw.setMode('select');
  }

  private initDrawing(): void {
    const adapter = new TerraDrawGoogleMapsAdapter({
      lib: google.maps,
      map: this.map,
      coordinatePrecision: 9,
    });

    this.terraDraw = new TerraDraw({
      adapter,
      modes: [
        new TerraDrawFreehandMode({
          minDistance: 30,
          validation: ValidateNotSelfIntersecting,
        }),
        new TerraDrawSelectMode({
          flags: {
            freehand: {
              feature: {
                draggable: true,
                rotateable: false,
                scaleable: false,
                coordinates: {
                  midpoints: false,
                  draggable: true,
                  deletable: true,
                },
              },
            },
          },
        }),
      ],
    });

    this.terraDraw.on('finish', this.onFinishDrawing.bind(this));

    this.terraDraw.start();
  }

  drawRegion(): void {
    if (this.terraDraw === null) {
      this.initDrawing();
    }
    this.terraDraw.clear();
    this.terraDraw.setMode('freehand');
  }

  mapClick(): void {
    if (this.previousIW) {
      this.previousIW.close();
    }
  }

  handleZoomChange(zoom: number): void {
    this.currentMapZoom = zoom;
    this.calculateAppointmentInfoLatitudeOffset();
  }

  private calculateAppointmentInfoLatitudeOffset(): void {
    this.appointmentInfoLatitudeOffset = Math.pow(
      2,
      this.APPOINTMENT_INFO_LATITUDE_BASE_EXPONENT - this.currentMapZoom
    );
    this.previewPopupLatitudeOffset = Math.pow(
      2,
      this.PREVIEW_POPUP_LATITUDE_OFFSET - this.currentMapZoom
    );
  }

  centerOnMarker(group: ItemGroup): void {
    this.mapContainerScrollAnchor.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    var latLng = new google.maps.LatLng(
      group.lat + this.previewPopupLatitudeOffset,
      group.lng
    );
    this.map.panTo(latLng);
  }

  markerClick(infoWindow: NgMapsInfoWindowComponent, group: ItemGroup): void {
    this.mapContainerScrollAnchor.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    var latLng = new google.maps.LatLng(
      group.lat + this.appointmentInfoLatitudeOffset,
      group.lng
    );
    this.map.panTo(latLng);

    group.clicked = true;

    // Check if there's a last activated preview installer and use it for prefilling
    const lastActivatedPreviewInstaller =
      this.tourPreviewStateService.getLastActivatedPreviewInstaller();
    const planValuesToUse = lastActivatedPreviewInstaller
      ? { selectedPlacer: lastActivatedPreviewInstaller }
      : this.lastSelectedPlanValues;

    this.groupSelected.next({
      group: group,
      lastSelectedPlanValues: planValuesToUse,
    });
    if (this.previousIW) {
      this.currentIW = infoWindow;
      this.previousIW.close();
    }
    this.previousIW = infoWindow;
    this.previousIWPreviewSlot = null;
  }

  setGroupHovering(group: ItemGroup, hovering: boolean): void {
    group.hovering = hovering;
  }

  closeInfoWindow(): void {
    if (this.currentIW != null) {
      this.currentIW.close();
      this.currentIW = null;
    }
    if (this.previousIW != null) {
      this.previousIW.close();
      this.previousIW = null;
    }
  }

  lastItemForGroupPlanned(): void {
    this.closeInfoWindow();
    this.itemBounds = ImmoMapHelper.getBoundsForItemGroups(this.itemGroups);
  }

  previewMarkerClick(
    previewSlot: PreviewSlot,
    infoWindow: NgMapsInfoWindowComponent,
    previewMarker: TourPreviewMarker
  ): void {
    if (
      previewMarker.subProjectId == null &&
      previewMarker.immoTaskId == null
    ) {
      return;
    }

    this.mapContainerScrollAnchor.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    var latLng = new google.maps.LatLng(
      previewMarker.lat + this.previewPopupLatitudeOffset,
      previewMarker.lng
    );
    this.map.panTo(latLng);

    previewMarker.clicked = true;

    // Check if there's a last activated preview installer and use it for prefilling
    const lastActivatedPreviewInstaller =
      this.tourPreviewStateService.getLastActivatedPreviewInstaller();
    const planValuesToUse = lastActivatedPreviewInstaller
      ? { selectedPlacer: lastActivatedPreviewInstaller }
      : this.lastSelectedPlanValues;

    this.previewMarkerSelected.next({
      subProjectId: previewMarker.subProjectId,
      immoTaskId: previewMarker.immoTaskId,
      lastSelectedPlanValues: planValuesToUse,
    });
    if (this.previousIW) {
      this.currentIW = infoWindow;
      this.previousIW.close();
    }
    this.previousIW = infoWindow;
    this.previousIWPreviewSlot = previewSlot;
  }

  itemReplanned(item: ImmoMapItem): void {
    this.closeInfoWindow();
    if (item != null) {
      const result: ProcessUnassignedItemResult =
        ImmoMapHelper.processUnassignedItem(
          item,
          this.itemGroups,
          this.coordsToGroup,
          this.itemBounds
        );
      this.itemBounds = result.latLngBounds;
      if (result.group != null) {
        this.itemAddedToGroup.next(result.group);
      }
    }
  }

  setSelectedPlanValues(lastSelectedPlanValues: LastSelectedPlanValues): void {
    this.lastSelectedPlanValues = lastSelectedPlanValues;
  }

  selectedPlacementTypesChanged(): void {
    this.centerMapOnItems = true;
    this.getItemsSubject.next();
  }

  allSelectedChanged(): void {
    if (this.allSelected) {
      this.withDateSelected = false;
      this.baseProjectSelected = false;
      this.adjustmentSelected = false;
      this.soldRentedSelected = false;
      this.repoSelected = false;
      this.notInTourSelected = false;
      this.alreadyInTourSelected = false;
    }
    this.centerMapOnItems = true;
    this.getItemsSubject.next();
  }

  filterTypeSelectedChanged(): void {
    this.allSelected = false;
    this.centerMapOnItems = true;
    this.getItemsSubject.next();
  }

  filterByBranch(branchName: string): void {
    const isCurrentlyActive = this.branchPreviewStates.get(branchName) || false;
    const newState = !isCurrentlyActive;

    if (newState) {
      this.tourPreviewStateService.resetPreviewStates();

      this.branchPreviewStates.forEach((_, key) => {
        this.branchPreviewStates.set(key, false);
      });
    }

    this.branchPreviewStates.set(branchName, newState);

    // Update selected branch filter for visual feedback
    this.selectedBranchFilter = newState ? branchName : null;

    // Use setTimeout to ensure the reset has propagated through observables
    // before emitting the branch toggle event
    setTimeout(() => {
      this.tourPlanningEventService.getBranchPreviewToggleSubject().next({
        branchName: branchName,
        showPreview: newState,
      });
    }, 0);
  }

  centerItems(): void {
    this.centerMapOnBounds(this.itemBounds);
  }

  private centerMapOnBounds(bounds: google.maps.LatLngBounds): void {
    if (bounds == null) {
      this.centerMapOnBelgium();
    } else {
      this.agmMap.fitBounds = bounds.toJSON();
      this.agmMap.triggerResize(true);
    }
  }

  private processTourPreviewStateChange(previewSlots: PreviewSlot[]): void {
    if (this.previousIWPreviewSlot != null) {
      this.closeInfoWindow();
      this.previousIWPreviewSlot = null;
    }

    for (const previewSlot of previewSlots) {
      previewSlot.markers = this.getPreviewMarkers(
        previewSlot.state?.placerFirstName || '',
        previewSlot.state?.placerAddressCoordinatesIncluded || false,
        previewSlot.state?.previewInfo || []
      );
      if (CollectionHelper.isPopulated(previewSlot.markers)) {
        this.renderDirections(previewSlot);
      }
    }

    const previousPreviewSlots = this.previewSlots;
    this.previewSlots = previewSlots;

    if (
      ImmoMapHelper.shouldCenterOnTourPreviews(
        previousPreviewSlots,
        this.previewSlots
      )
    ) {
      this.centerMapOnItems = false;
      this.processTourPreviewBounds();
    } else if (
      !CollectionHelper.isPopulated(this.previewSlots) ||
      !this.previewSlots.some((s) => CollectionHelper.isPopulated(s.markers))
    ) {
      this.centerMapOnItems = true;
      this.centerItems();
    }
  }

  private processTourPreviewBounds(): void {
    this.tourPreviewBounds = ImmoMapHelper.getBoundsForTourPreviewMarkers(
      this.previewSlots
    );
    if (this.tourPreviewBounds != null) {
      this.centerMapOnBounds(this.tourPreviewBounds);
    }
  }

  private getPreviewMarkers(
    placerName: string,
    placerAddressCoordinatesIncluded: boolean,
    previewInfo: PreviewInfo[]
  ): TourPreviewMarker[] {
    if (
      StringHelper.isEmpty(placerName) ||
      !CollectionHelper.isPopulated(previewInfo)
    ) {
      return [];
    }
    const indexAdjustment = placerAddressCoordinatesIncluded ? 0 : 1;
    return previewInfo.map((info, i) => {
      return {
        subProjectId: info.subProjectId,
        immoTaskId: info.immoTaskId,
        clicked: false,
        hovering: false,
        label:
          i === 0
            ? placerName
            : info.collectKey
            ? ' '
            : (i + indexAdjustment).toString(),
        pathSuffix: this.getPreviewMarkerPathSuffix(info, i),
        labelOrigin: new google.maps.Point(
          i === 0 ? 35 : 14,
          i === 0 ? 33.5 : 16
        ),
        lat: info.coordinates.lat,
        lng: info.coordinates.lng,
      };
    });
  }

  private getPreviewMarkerPathSuffix(info: PreviewInfo, index: number): string {
    return info.onStones
      ? index === 0
        ? '-stenen-big.png'
        : '-stenen.png'
      : info.collectKey
      ? '-sleutel.png'
      : index === 0
      ? '-big.png'
      : '.png';
  }

  private renderDirections(previewSlot: PreviewSlot): void {
    if (!CollectionHelper.isPopulated(previewSlot.markers)) {
      return;
    }
    this.resetDirectionsRenderer(previewSlot.directionsRenderer);
    this.employeeTourMapDirectionsCalculationService
      .calculateDirections(previewSlot.markers, this.destroyThisComponent)
      .then((result: google.maps.DirectionsResult) => {
        this.calculateTotalDistance(previewSlot, result);
        previewSlot.routeBounds = result.routes[0].bounds;
        return previewSlot.directionsRenderer.setDirections(result);
      })
      .catch((err) => console.error(err));
  }

  private calculateTotalDistance(
    previewSlot: PreviewSlot,
    result: google.maps.DirectionsResult
  ): void {
    let total = 0;
    result.routes[0].legs.forEach((l) => {
      total += l.distance?.value || 0;
    });
    let roundedKm = (total * 0.001).toFixed(2);
    previewSlot.totalDistance =
      roundedKm.replace('.00', '').replace('.', ',') + ' km';
  }

  private resetDirectionsRenderer(
    directionsRenderer: google.maps.DirectionsRenderer
  ): void {
    directionsRenderer.setDirections({
      geocoded_waypoints: [],
      routes: [],
    });
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }
}
